import React, { useEffect, useState } from 'react';

import {
  Typography,
  Descriptions,
  Popconfirm,
  Modal,
  Skeleton,
  notification,
  Alert,
  Button,
} from 'antd';
import { Link } from 'react-router-dom';
import BreadcrumbContainer from '../../containers/Breadcrumb';
import AjaxTable from '../../components/AjaxTable';
import {
  add_payment,
  finalize_pending,
  get,
  getRealValue,
  paymentsTable,
} from '../../api/module/product_approval';

import '../../styles/main.scss';
import { CustomButton } from '../../components';
import { catchException, useParsedSearch } from '../../utils/helpers/helpers';
import INTERNAL_LINKS from '../../enums/InternalLinks';
import { useParams } from 'react-router-dom';
import { CustomTooltip } from '../../components/CustomTooltip';
import PAPaymentForm from './PaymentForm';

import MESSAGES from '../../enums/Messages';
import dayjs from 'dayjs';
import { EyeOutlined } from '@ant-design/icons';

const { Title } = Typography;

const breadcrumbItems = [
  {
    text: 'Aprobación de productos y/o entregables y gastos',
    to: INTERNAL_LINKS.PRODUCT_APPROVAL,
  },
  {
    text: 'Formulario de aprobaciones',
  },
];

export default function ProductApprovalForm({ pcPerm, editPerm }) {
  let { id, pc } = useParams();

  const { person: fromPerson } = useParsedSearch({
    person: ['True'],
  });

  const [initialValues, setInitialValues] = useState();
  const [expandedKeys, setExpandedKeys] = useState([]);
  const [reloadSource, setReloadSource] = useState(false);
  const [fpLoading, setFPLoading] = useState(false);

  useEffect(() => {
    async function fetchData() {
      try {
        const res = await get(id);
        setInitialValues({
          ...res.data,
          acumulado_referencia: res.data.acumulado_referencia || 0,
        });
      } catch (error) {
        catchException(error);
      }
    }
    fetchData();
  }, [id]);

  useEffect(() => {
    let isCurrent = true;
    async function fetchRV() {
      const { data } = await getRealValue(initialValues.id);
      if (isCurrent) setInitialValues((s) => ({ ...s, ...data }));
    }
    if (reloadSource.hasOwnProperty('paymentID')) {
      fetchRV();
    }
    return () => {
      isCurrent = false;
    };
  }, [reloadSource]);

  const paymentColumns = [
    {
      title: 'No. de pago',
      dataIndex: 'numero',
    },
    {
      title: 'Valor del pago (incluyendo impuestos, tasas y contribuciones)',
      width: '16rem',
      dataIndex: 'valor',
      render: (t) =>
        `${initialValues?.moneda_s} ${t
          .toFixed(2)
          .replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`,
    },
    {
      title: (
        <>
          Estado de aprobación
          <CustomTooltip
            onClick={() =>
              Modal.info({
                title: 'Estados se calculan automático:',
                content: (
                  <ul>
                    <li>
                      Aprobado: Todos los estados de los productos/gastos son
                      Aprobado total
                    </li>
                    <li>
                      Aprobado temporal: (no es definitivo-es editable el
                      estado): Al menos un producto/gasto sea Condicionado o
                      Pendiente
                    </li>
                    <li>
                      Aprobado con novedad: (definitivo-sin edición el estado):
                      Al menos un producto/gasto es Aprobado incompleto,
                      Aprobado parcial, No aprobado y/o No entregado
                    </li>
                    <li>
                      No entregado: Todos los productos/gastos de ese pago es
                      "No entregado"
                    </li>
                    <li>
                      No aprobado: Los únicos estados para los productos/gastos
                      son "No entregado" y "No aprobado"
                    </li>
                    <li>Borrador: Aún no se envía a pago </li>
                    <li>
                      Sin gestionar: Todos los productos/gastos no tienen estado
                    </li>
                  </ul>
                ),
              })
            }
            title={<>Estados se calculan automático...</>}
          />
        </>
      ),
      dataIndex: 'estado_nombre',
      render: (t, record) => (
        <>
          {t}{' '}
          {record.eb_nombre ? (
            <CustomTooltip
              title={`El estado en borrador es: ${record.eb_nombre}`}
            />
          ) : null}
        </>
      ),
    },
    {
      title: 'Valor real aprobado',
      dataIndex: 'valor_a_pagar',
      render: (t) =>
        t
          ? `${initialValues?.moneda_s} ${t
              .toFixed(2)
              .replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`
          : null,
    },
    {
      title: 'Acciones',
      key: 'actions',
      fixed: 'right',
      align: 'center',
      render: () => (
        <Button icon={<EyeOutlined />} type="primary" htmlType="button" />
      ),
    },
  ];

  const stColumns = [
    {
      title: 'No. de aprobación',
      dataIndex: 'numero',
    },
    {
      title: (
        <>
          Estado de aprobación
          <CustomTooltip
            onClick={() =>
              Modal.info({
                title: 'Estados se calculan automático:',
                content: (
                  <ul>
                    <li>
                      Aprobado: Todos los estados de los productos/gastos son
                      Aprobado total
                    </li>
                    <li>Borrador: Aún no se envía a pago </li>
                    <li>
                      Sin gestionar: Todos los productos/gastos no tienen estado
                    </li>
                  </ul>
                ),
              })
            }
            title={<>Estados se calculan automático...</>}
          />
        </>
      ),
      dataIndex: 'estado_nombre',
      render: (t, record) => (
        <>
          {t}{' '}
          {record.eb_nombre ? (
            <CustomTooltip
              title={`El estado en borrador es: ${record.eb_nombre}`}
            />
          ) : null}
        </>
      ),
    },
    {
      title: 'Valor para este pago',
      dataIndex: 'valor_a_pagar',
      render: (t) =>
        `${initialValues?.moneda_s} ${(t || 0)
          .toFixed(2)
          .replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`,
    },
    {
      title: 'Acciones',
      key: 'actions',
      fixed: 'right',
      align: 'center',
      render: () => (
        <Button icon={<EyeOutlined />} type="primary" htmlType="button" />
      ),
    },
  ];

  const expandedRowRender = (record) => {
    return (
      <PAPaymentForm
        record={record}
        contract={initialValues}
        setAcumulated={setAcumulated}
        setReloadForm={setReloadSource}
        reloadForm={reloadSource}
        editPerm={editPerm}
      />
    );
  };

  const setAcumulated = (value) => {
    setInitialValues((s) => ({ ...s, acumulado_referencia: value }));
  };

  const onAddPayment = async () => {
    try {
      await add_payment(initialValues.id);
      setReloadSource({});
      notification.success({
        message: MESSAGES.SUCCESS_MSG,
        description: MESSAGES.DESC_SUCCESS_MSG,
        duration: 2,
      });
    } catch (error) {
      catchException(error);
    }
  };

  const onFinalizePending = async () => {
    try {
      setFPLoading(true);
      await finalize_pending(initialValues.id);
      setReloadSource({});
      notification.success({
        message: MESSAGES.SUCCESS_MSG,
        description: MESSAGES.DESC_SUCCESS_MSG,
        duration: 2,
      });
    } catch (error) {
      catchException(error);
    }
    setFPLoading(false);
  };

  return (
    <>
      <BreadcrumbContainer items={breadcrumbItems} />
      <Title className={'text-uppercase'} level={2}>
        Aprobación de productos y/o entregables y gastos
      </Title>
      {initialValues ? (
        <>
          <Descriptions
            layout="horizontal"
            column={3}
            bordered
            labelStyle={{ maxWidth: '8rem' }}
            size="small"
          >
            <Descriptions.Item label="Nombre completo / Razón social" span={2}>
              {initialValues?.nombre_completo}
            </Descriptions.Item>
            <Descriptions.Item label="Documento">
              {initialValues?.tipo_identificacion}{' '}
              {initialValues?.numero_identificacion}
            </Descriptions.Item>
            <Descriptions.Item label="Proyecto" span={3}>
              {initialValues?.proyecto_nombre}
            </Descriptions.Item>
            <Descriptions.Item label="Subcentro de costos" span={3}>
              {initialValues?.centro_costo_nombre}
            </Descriptions.Item>
            <Descriptions.Item label="Cargo/Rol" span={3}>
              {initialValues?.cargo_nombre}
            </Descriptions.Item>
            <Descriptions.Item label="No. de contrato">
              {initialValues?.numero_contrato}
            </Descriptions.Item>
            <Descriptions.Item label="No. de modificaciones">
              {initialValues?.mod_num ?? 0}
            </Descriptions.Item>
            <Descriptions.Item label="Moneda del contrato">
              {initialValues?.moneda_s} - {initialValues?.moneda_d}
            </Descriptions.Item>
            <Descriptions.Item label="Identificador del TDR" span={3}>
              {initialValues?.ii}
            </Descriptions.Item>
            <Descriptions.Item label="Fecha de inicio">
              {initialValues?.fecha_inicio
                ? dayjs(initialValues.fecha_inicio).format('DD MMMM YYYY')
                : null}
            </Descriptions.Item>
            <Descriptions.Item label="Fecha de finalización VIGENTE" span={2}>
              {initialValues?.fecha_fin
                ? dayjs(initialValues.fecha_fin).format('DD MMMM YYYY')
                : null}
            </Descriptions.Item>
            {initialValues?.valor_contrato > 0 ? (
              <>
                <Descriptions.Item
                  label={
                    <>
                      Valor total del Contrato VIGENTE{' '}
                      <CustomTooltip title="Productos/Gastos Fijos o Variables con Tope" />
                    </>
                  }
                >
                  {initialValues?.moneda_s}{' '}
                  {(initialValues?.valor_contrato || 0)
                    .toString()
                    .replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                </Descriptions.Item>
                <Descriptions.Item
                  label={
                    <>
                      Valor real total APROBADO{' '}
                      <CustomTooltip title="Valor aprobado de Productos/Gastos Fijos o Variables con Tope" />
                    </>
                  }
                  span={2}
                >
                  {initialValues?.moneda_s}{' '}
                  {(initialValues?.valor_real || 0)
                    .toString()
                    .replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                </Descriptions.Item>
              </>
            ) : null}
            {initialValues.valor_referencia > 0 ? (
              <>
                <Descriptions.Item
                  label={
                    <>
                      Valor máximo de referencia del Contrato (P/G sin tope){' '}
                      <CustomTooltip title="Productos/Gastos Variables sin Tope" />
                    </>
                  }
                >
                  {initialValues.moneda_s}{' '}
                  {initialValues.valor_referencia
                    .toString()
                    .replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                </Descriptions.Item>
                <Descriptions.Item
                  label={
                    <>
                      Sumatoria de valores máximos de referencia acumulados{' '}
                      <CustomTooltip title="Valor aprobado de Productos/Gastos Variables sin Tope" />
                    </>
                  }
                  span={2}
                >
                  {initialValues.moneda_s}{' '}
                  {initialValues.acumulado_referencia
                    .toString()
                    .replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                </Descriptions.Item>
              </>
            ) : null}
          </Descriptions>
          {pcPerm && editPerm && pc ? (
            <>
              <br />
              <Popconfirm
                title={
                  <>
                    Esta acción marcará los productos sin gestionar aprobación
                    como NO ENTREGADOS.
                    <br />
                    Si continúa esta acción no se podrá deshacer.
                    <br />
                    ¿Está seguro(a) de continuar?
                  </>
                }
                okText="Si"
                cancelText="No"
                onConfirm={onFinalizePending}
              >
                <CustomButton
                  type="danger"
                  htmlType="button"
                  text="Gestionar productos pendientes"
                  loading={fpLoading}
                  disabled={initialValues.has_pc}
                />
              </Popconfirm>
              <br />
            </>
          ) : null}
          {initialValues.unicamente_sin_tope ? (
            <>
              <br />
              <Popconfirm
                title={
                  <>
                    Esta acción impedirá que se realicen modificaciones
                    contractuales o liquidaciones hasta que se envié a pago.
                    <br />
                    Si continúa esta acción no se podrá deshacer.
                    <br />
                    ¿Está seguro(a) de continuar?
                  </>
                }
                okText="Si"
                cancelText="No"
                onConfirm={() => onAddPayment()}
              >
                <CustomButton
                  type="primary"
                  htmlType="button"
                  text="Agregar aprobación para pago"
                  disabled={initialValues.has_pc || !editPerm}
                  style={{ marginBottom: 10 }}
                />
              </Popconfirm>
              <br />
              <Alert
                message="Información:"
                description={
                  <>
                    <ul>
                      <li>
                        El valor máximo de referencia del tope interno:{' '}
                        {initialValues.moneda_s}{' '}
                        {initialValues.valor_referencia
                          .toString()
                          .replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                      </li>
                      <li>
                        Valor acumulado aprobado a la fecha:{' '}
                        {initialValues.moneda_s}{' '}
                        {initialValues.acumulado_referencia
                          .toString()
                          .replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                      </li>
                    </ul>
                  </>
                }
                type={
                  initialValues.valor_referencia -
                    initialValues.acumulado_referencia !==
                  0
                    ? 'info'
                    : 'success'
                }
              />
            </>
          ) : null}
          <AjaxTable
            columns={
              initialValues.unicamente_sin_tope ? stColumns : paymentColumns
            }
            endpoint={paymentsTable}
            query={initialValues.id}
            expandedRowRender={{
              expandedRowRender,
              expandRowByClick: true,
              indentSize: 5,
              expandedRowKeys: expandedKeys,
              onExpandedRowsChange: (v) => setExpandedKeys(v),
            }}
            onSearch={(ds) => setExpandedKeys(ds.map((v) => v.id))}
            tableTitle="Listado de pagos"
            searchInput
            exportCSV
            reloadSource={reloadSource}
          />
          <Descriptions
            layout="horizontal"
            column={2}
            bordered
            labelStyle={{ maxWidth: '8rem' }}
            size="small"
          >
            {initialValues?.valor_contrato > 0 ? (
              <>
                <Descriptions.Item
                  label={
                    <>
                      Valor total del Contrato VIGENTE{' '}
                      <CustomTooltip title="Productos/Gastos Fijos o Variables con Tope" />
                    </>
                  }
                >
                  {initialValues?.moneda_s}{' '}
                  {(initialValues?.valor_contrato || 0)
                    .toString()
                    .replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                </Descriptions.Item>
                <Descriptions.Item
                  label={
                    <>
                      Valor real total APROBADO{' '}
                      <CustomTooltip title="Valor aprobado de Productos/Gastos Fijos o Variables con Tope" />
                    </>
                  }
                  span={2}
                >
                  {initialValues?.moneda_s}{' '}
                  {(initialValues?.valor_real || 0)
                    .toString()
                    .replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                </Descriptions.Item>
              </>
            ) : null}

            {initialValues?.valor_referencia > 0 ? (
              <>
                <Descriptions.Item
                  label={
                    <>
                      Valor máximo de referencia del Contrato (P/G sin tope){' '}
                      <CustomTooltip title="Productos/Gastos Variables sin Tope" />
                    </>
                  }
                >
                  {initialValues.moneda_s}{' '}
                  {initialValues.valor_referencia
                    .toString()
                    .replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                </Descriptions.Item>
                <Descriptions.Item
                  label={
                    <>
                      Sumatoria de valores máximos de referencia acumulados{' '}
                      <CustomTooltip title="Valor aprobado de Productos/Gastos Variables sin Tope" />
                    </>
                  }
                  span={2}
                >
                  {initialValues.moneda_s}{' '}
                  {initialValues.acumulado_referencia
                    .toString()
                    .replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                </Descriptions.Item>
              </>
            ) : null}
          </Descriptions>
          <br />

          <CustomButton
            type="default"
            htmlType="button"
            className="back-button"
            text={
              <Link
                to={
                  pc
                    ? `${INTERNAL_LINKS.POSTCONTRACTUAL}-new/${initialValues.proyecto_id}`
                    : fromPerson
                      ? `${INTERNAL_LINKS.PRODUCT_APPROVAL}/person=${initialValues.tercero_id}`
                      : `${INTERNAL_LINKS.PRODUCT_APPROVAL}/project=${initialValues.proyecto_id}`
                }
              >
                Volver
              </Link>
            }
          />
        </>
      ) : (
        <Skeleton loading active />
      )}
    </>
  );
}
